import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './BuildStartForProfessionals.module.css'; // We'll create this CSS file next

// Import icons
import { FaPlay, FaRegListAlt, FaRegHandshake, FaRegSmile, FaRegStar } from 'react-icons/fa';

// --- Reusable Header/Footer for consistency ---
const ProfessionalsHeader = () => (
  <header className={styles.header}>
    <div className={styles.container}>
      <Link to="/" className={styles.logo}>Build Start</Link>
      <nav className={styles.nav}>
        <Link to="#how-it-works">How It Works</Link>
        <Link to="#pricing">Pricing</Link>
        <Link to="#testimonials">Testimonials</Link>
      </nav>
      <div className={styles.headerActions}>
        <Link to="/login" className={styles.loginBtn}>Log In</Link>
        <Link to="/select-role" className={styles.getStartedBtn}>Get Started</Link>
      </div>
    </div>
  </header>
);

// --- Page-Specific Components ---

const Hero = () => (
  <section className={styles.hero}>
    <div className={styles.heroContent}>
      <h1>Stop Chasing Work. <br />Let Work Chase You.</h1>
      <p>Connect with thousands of serious homeowners posting jobs on Build Start every day. We send you quality leads so you can focus on what you do best.</p>
      <div className={styles.leadForm}>
        <input type="text" placeholder="Enter your business postcode" />
        <button>Get Started</button>
      </div>
    </div>
    <div className={styles.heroVideo}>
      {/* Image Prompt: A friendly, professional-looking tradesperson smiling confidently at the camera. Maybe holding a tool or tablet. The background should be a clean work site or a branded van. */}
      <img src="https://via.placeholder.com/600x450.png?text=Video+of+a+Successful+Tradie" alt="Hear from a successful professional on Build Start" />
      <div className={styles.playButton}>
        <FaPlay />
      </div>
      <div className={styles.videoCaption}>
        <h4>Hear Martin's success on Build Start</h4>
        <p>Martin Nguyen, Kubra Building Services</p>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section id="how-it-works" className={styles.howItWorks}>
    <div className={styles.container}>
      <h2>Spend Less Time Quoting, More Time Working</h2>
      <div className={styles.stepsGrid}>
        <div className={styles.step}>
          <div className={styles.stepIcon}><FaRegListAlt /></div>
          <h3>1. Get Job Leads Sent to Your Phone</h3>
          <p>When clients post a job that matches your skills and area, we send the lead straight to you. No more chasing dead ends.</p>
        </div>
        <div className={styles.step}>
          <div className={styles.stepIcon}><FaRegHandshake /></div>
          <h3>2. You Choose the Jobs You Want</h3>
          <p>You're in control. Review the job details and decide if it's the right fit. Accept the ones you want and get in touch to quote.</p>
        </div>
        <div className={styles.step}>
          <div className={styles.stepIcon}><FaRegSmile /></div>
          <h3>3. Win the Work & Get It Done</h3>
          <p>Use our tools to quote, schedule, invoice, and accept payments, all within the Build Start app. It's your entire admin in one place.</p>
        </div>
        <div className={styles.step}>
          <div className={styles.stepIcon}><FaRegStar /></div>
          <h3>4. Get Recommended by Your Clients</h3>
          <p>Great work leads to great reviews. We help you collect verified recommendations to build your reputation and win even more work.</p>
        </div>
      </div>
    </div>
  </section>
);

const Testimonials = () => (
    <section id="testimonials" className={styles.testimonials}>
        <div className={styles.container}>
            <h2>Don't Just Take Our Word For It</h2>
            <div className={styles.testimonialCard}>
                {/* Image Prompt: An authentic photo of a tradesperson (like Hayden from the hipages example) standing proudly in front of their work van or a completed project. */}
                <img src="https://via.placeholder.com/400x350.png?text=Photo+of+Hayden" alt="Hayden from Wattley Air and Electrical" />
                <div className={styles.testimonialContent}>
                    <p className={styles.quoteIcon}>"</p>
                    <blockquote>
                    We started our business with nothing. No savings or building contracts so you can imagine it was very stressful diving into the unknown. We used Build Start to get us off the ground. As more work came in, we bumped up our membership to get access to more job leads. Our business currently has three employees.
                    </blockquote>
                    <cite>
                        <strong>Hayden, Wattley Air and Electrical</strong>
                    </cite>
                </div>
            </div>
        </div>
    </section>
);

const Faq = () => {
    // A simple state for toggling FAQ items could be added here
    return (
        <section className={styles.faq}>
            <div className={styles.container}>
                <h2>Popular Questions</h2>
                <div className={styles.faqItem}>
                    <h3>How do I join Build Start?</h3>
                    <p>It's simple! Click "Get Started", enter a few details about your business, and our team will be in touch to help you build a winning profile.</p>
                </div>
                <div className={styles.faqItem}>
                    <h3>How does pricing work?</h3>
                    <p>We offer flexible membership packages based on your needs. Our plans include a set amount of lead credits per month, giving you control over your spending. There are no lock-in contracts for our most popular plans.</p>
                </div>
                 <div className={styles.faqItem}>
                    <h3>What trades can list on Build Start?</h3>
                    <p>We welcome over 150 trades and services, from plumbers and electricians to painters and landscape gardeners. If you work in the home improvement industry, there's a place for you here.</p>
                </div>
            </div>
        </section>
    );
};


const FinalCta = () => (
    <section className={styles.finalCta}>
        <div className={styles.container}>
            <div className={styles.ctaContent}>
                <h2>Start your Build Start journey.</h2>
                <h3>Let work come to you.</h3>
                <p>With a new job posted every 25 seconds on average, Build Start is the place to be for job leads and job management. We send you the leads, you choose the jobs, you stay in control.</p>
                <Link to="/select-role" className={styles.getStartedBtn}>Get Started</Link>
            </div>
            <div className={styles.ctaImage}>
                {/* Image Prompt: A vibrant, happy photo of a tradesperson working on a job site, smiling. Should feel authentic and positive. */}
                <img src="https://via.placeholder.com/500x500.png?text=Happy+Tradie+Working" alt="Let work come to you" />
            </div>
        </div>
    </section>
);


const ProfessionalsFooter = () => (
    <footer className={styles.footer}>
        {/* A simplified footer can be used here, similar to the main one */}
        <div className={styles.container}>
            <p>© 2025 Build Start | All Rights Reserved</p>
        </div>
    </footer>
)


// --- Main Component ---
const ForProfessionalsPage = () => {
  return (
    <div className={styles.pageWrapper}>
      <ProfessionalsHeader />
      <main>
        <Hero />
        <HowItWorks />
        <Testimonials />
        <Faq />
        <FinalCta />
      </main>
      <ProfessionalsFooter />
    </div>
  );
};

export default ForProfessionalsPage;